import { ownedRequestLifecycleStates } from "./request-lifecycle.js";
// The staff request queue (CHT-750, contract section 7.2). These are the RULES of the queue: the closed filter names, which
// requests each filter holds, who sees a request, the order, and the attention flags. The Postgres query is the real reader
// and a database test proves it agrees with `requestMatchesQueueFilter` on a seeded set of every state. There is NO service-level
// number here: aging is data (`ageSeconds`, `awaitingCustomerSince`), and the only knob is the optional
// `LAFAYETTE_REQUEST_AGING_MINUTES` threshold, which the composition passes in as `agingMinutes` (`null`: no `AGING` flag).
export const staffRequestQueueFilters = Object.freeze([
    "ASSIGNED_TO_ME",
    "UNASSIGNED",
    "SUBMITTED",
    "IN_PROGRESS",
    "AWAITING_CUSTOMER",
    "AWAITING_SUPPLIER",
    "PAYMENT_REQUIRED",
    "CONFIRMED",
    "COMPLETED",
    "UNAVAILABLE_OR_CANCELLED",
    "AGING",
]);
/** A queue row's attention flags. Closed: the admin UI renders exactly these. */
export const staffRequestAttentionFlags = Object.freeze([
    "UNASSIGNED",
    "AWAITING_CUSTOMER",
    "AGING",
]);
/** The explicit operational exceptions of a request. `COVERAGE_GAP`: submitted, and routing left it with no owner. */
export const staffRequestExceptions = Object.freeze(["COVERAGE_GAP"]);
export const STAFF_REQUEST_QUEUE_LIMIT_DEFAULT = 25;
export const STAFF_REQUEST_QUEUE_LIMIT_MAX = 100;
/** A page size inside the bounds; anything that is not a finite number is the default. */
export function boundedQueueLimit(limit) {
    if (limit === null || limit === undefined || !Number.isFinite(limit)) {
        return STAFF_REQUEST_QUEUE_LIMIT_DEFAULT;
    }
    return Math.min(Math.max(Math.trunc(limit), 1), STAFF_REQUEST_QUEUE_LIMIT_MAX);
}
/** The states a queue shows by default (no filter): everything a person may still act on. Never a DRAFT. */
export const openRequestStates = Object.freeze([
    "SUBMITTED",
    "ASSIGNED",
    "IN_PROGRESS",
    "AWAITING_CUSTOMER",
    "PAYMENT_REQUIRED",
    "CONFIRMED",
]);
/** The states in which a request has an owner (`owner_staff_user_id` is set: the owner CHECK). */
export const ownedRequestStates = ownedRequestLifecycleStates;
/** `UNAVAILABLE_OR_CANCELLED` holds every request that ended without being fulfilled: UNAVAILABLE, CANCELLED, DECLINED and EXPIRED. */
export const unfulfilledRequestStates = Object.freeze([
    "UNAVAILABLE",
    "CANCELLED",
    "DECLINED",
    "EXPIRED",
]);
/** The states in which the next move is staff's, so age counts as lateness (`AGING`). A request that waits for the customer or a payment is not late. */
export const staffMoveRequestStates = Object.freeze([
    "SUBMITTED",
    "ASSIGNED",
    "IN_PROGRESS",
]);
/** Closed filters list the newest change first; every working filter lists the request that has waited longest first. */
export function staffRequestQueueOrder(filter) {
    return filter === "COMPLETED" || filter === "UNAVAILABLE_OR_CANCELLED"
        ? "NEWEST_FIRST"
        : "OLDEST_FIRST";
}
/** Seconds since the request was submitted; `null` for a request that never was. Not an SLA: a number the screen shows. */
export function requestAgeSeconds(submittedAt, now) {
    if (submittedAt === null)
        return null;
    return Math.max(0, Math.floor((now.getTime() - submittedAt.getTime()) / 1000));
}
/**
 * Whether the viewer sees the request in the queue. A supervisor sees every request that is not a draft. A concierge sees the
 * requests that are theirs (designated to them while ASSIGNED, owned by them while open) and the UNASSIGNED requests of a customer
 * they hold an effective assignment to (a PRIMARY, BACKUP or TEMPORARY concierge): that last clause is what lets a concierge see,
 * and claim, the shared-queue requests of their customers. A request another concierge works is not in a concierge's queue, even
 * for a customer both serve; it stays readable by id under the relationship rule of `getStaffRequestDetail`.
 */
export function isRequestVisibleToStaff(facts, viewer) {
    if (facts.state === "DRAFT")
        return false;
    if (viewer.isSupervisor)
        return true;
    if (facts.state === "ASSIGNED" && facts.assignedStaffUserId === viewer.staffUserId)
        return true;
    if (ownedRequestStates.includes(facts.state) && facts.ownerStaffUserId === viewer.staffUserId) {
        return true;
    }
    return isUnassignedRequest(facts) && viewer.assignedCustomerIds.has(facts.customerId);
}
/** No owner after submission: SUBMITTED and not yet routed, or ASSIGNED to the shared supervisor queue (no named assignee). */
export function isUnassignedRequest(facts) {
    return ((facts.state === "SUBMITTED" || facts.state === "ASSIGNED") &&
        facts.assignedStaffUserId === null);
}
function isAging(facts, context) {
    if (context.agingMinutes === null)
        return false;
    if (!staffMoveRequestStates.includes(facts.state))
        return false;
    const age = requestAgeSeconds(facts.submittedAt, context.now);
    return age !== null && age >= context.agingMinutes * 60;
}
/** The staff-facing filter. `viewerId` is needed by `ASSIGNED_TO_ME` only. */
export function requestMatchesQueueFilter(facts, filter, viewerId, context) {
    switch (filter) {
        case null:
            return openRequestStates.includes(facts.state);
        case "ASSIGNED_TO_ME":
            return ((facts.state === "ASSIGNED" && facts.assignedStaffUserId === viewerId) ||
                (ownedRequestStates.includes(facts.state) && facts.ownerStaffUserId === viewerId));
        case "UNASSIGNED":
            return isUnassignedRequest(facts);
        case "SUBMITTED":
        case "IN_PROGRESS":
        case "AWAITING_CUSTOMER":
        case "PAYMENT_REQUIRED":
        case "CONFIRMED":
        case "COMPLETED":
            return facts.state === filter;
        case "AWAITING_SUPPLIER":
            return facts.state === "IN_PROGRESS" && facts.workStatus === "WITH_SUPPLIER";
        case "UNAVAILABLE_OR_CANCELLED":
            return unfulfilledRequestStates.includes(facts.state);
        case "AGING":
            return isAging(facts, context);
    }
}
export function requestAttentionFlags(facts, context) {
    const flags = [];
    if (isUnassignedRequest(facts))
        flags.push("UNASSIGNED");
    if (facts.state === "AWAITING_CUSTOMER")
        flags.push("AWAITING_CUSTOMER");
    if (isAging(facts, context))
        flags.push("AGING");
    return flags;
}
export function requestException(facts) {
    return isUnassignedRequest(facts) ? "COVERAGE_GAP" : null;
}
